<!--
  PROPOSTA DI UN PACCHETTO VALET — prezzo dal listino.

  Stesso modello del gestionale partner (`PropostaValet.vue`):
    1. sede del partner scelto (se ne ha più d'una);
    2. distanza su strada dalla casa del cliente → fascia del listino valet;
    3. numero di pickup e durata in mesi → valet compresi, prezzo e scadenza.

  Sul pacchetto si salvano il prezzo Simba, quello al cliente (prezzo del
  venditore, quando il partner l'ha impostato) e la fascia: il backend
  ricalcola i prezzi dalla fascia. Importi senza IVA.

  Emette `change` con `{ problem, data }`: `data` è pronto per
  `valetAPI.minutePayload` quando `problem` è vuoto.
-->
<template>
  <div class="vp">
    <div class="vp__grid">
      <div
        v-if="headquarters.length > 1"
        class="vp__f vp__f--wide"
      >
        <label :for="`${uid}-hq`">Sede</label>
        <el-select
          :id="`${uid}-hq`"
          v-model="headquarterId"
          placeholder="Scegli la sede"
          style="width: 100%"
        >
          <el-option
            v-for="h in headquarters"
            :key="h.id"
            :label="hqLabel(h)"
            :value="h.id"
          />
        </el-select>
      </div>
      <!-- Il prezzo è per PICKUP, a scaglioni: il listino ha il prezzo di uno,
           di due e di tre o più. Sopra i tre vale il prezzo della terza riga. -->
      <div class="vp__f">
        <label :for="`${uid}-pickup`">Numero di pickup</label>
        <el-input-number
          :id="`${uid}-pickup`"
          v-model="pickup"
          :min="1"
          :max="99"
          step-strictly
          controls-position="right"
        />
      </div>
      <div class="vp__f">
        <label :for="`${uid}-months`">Durata</label>
        <el-select
          :id="`${uid}-months`"
          v-model="months"
          class="vp__months"
        >
          <el-option
            v-for="m in DURATE"
            :key="m"
            :label="`${m} mesi (${m / 12} ${m === 12 ? 'anno' : 'anni'})`"
            :value="m"
          />
        </el-select>
      </div>
    </div>

    <p
      class="vp__where"
      aria-live="polite"
    >
      <template v-if="status === 'loading'">
        Calcolo la distanza dalla sede…
      </template>
      <template v-else-if="result && result.band">
        {{ result.km.toLocaleString('it-IT') }} km su strada · fascia {{ bandLabel(result.band) }}
        <template v-if="result.scope === 'global'">
          · listino generale Simba
        </template>
      </template>
    </p>

    <div
      v-if="numbers"
      class="vp__stats"
    >
      <span>Valet compresi <b>{{ numbers.valetTotal }}</b></span>
      <span>Al cliente, a pickup <b>{{ numbers.sellerUnit === null ? '—' : `${euro(numbers.sellerUnit)} + IVA` }}</b></span>
      <span>Al cliente, in tutto <b>{{ numbers.sellerTotal === null ? '—' : `${euro(numbers.sellerTotal)} + IVA` }}</b></span>
      <!-- Rateizzazione sulla durata scelta: il prezzo non dipende dai mesi,
           ma «quanto viene al mese» è la prima domanda del cliente. -->
      <span>Al cliente, al mese su {{ numbers.months }} <b>{{ numbers.sellerMonthly === null ? '—' : `${euro(numbers.sellerMonthly)} + IVA` }}</b></span>
      <span>Pacchetto Simba <b>{{ numbers.simbaTotal === null ? '—' : `${euro(numbers.simbaTotal)} + IVA` }}</b></span>
      <span>Scade il <b>{{ numbers.expiry.toLocaleDateString('it-IT') }}</b></span>
    </div>

    <p
      v-if="problem && partnerId"
      class="vp__problem"
    >
      {{ problem }}
      <el-button
        v-if="status === 'error'"
        size="small"
        text
        @click="compute"
      >
        Riprova
      </el-button>
    </p>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import valetAPI from '../../services/valetAPI.js';

const clean = (v) => String(v ?? '').trim();

// La durata si sceglie di anno in anno, fino a otto: sono contratti annuali.
const DURATE = [12, 24, 36, 48, 60, 72, 84, 96];
const euroFmt = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
let sequence = 0;

export default {
  name: 'ValetProposal',

  props: {
    // Indirizzo del cliente: { address, street_number, cap, city, province }.
    address: { type: Object, required: true },
    // Partner del pacchetto: dà le sedi e il listino.
    partnerId: { type: String, default: '' },
  },

  emits: ['change'],

  setup(props, { emit }) {
    sequence += 1;
    const uid = `vp-${sequence}`;
    const headquarters = ref([]);
    const headquarterId = ref('');
    const pickup = ref(1);
    const months = ref(12);
    const status = ref('idle'); // idle | loading | ok | error
    const result = ref(null);
    const serverError = ref('');
    const hqStatus = ref('idle'); // idle | loading | ok | error

    // Sedi del partner: con una sola non si chiede.
    watch(() => props.partnerId, async (partnerId) => {
      headquarters.value = [];
      headquarterId.value = '';
      result.value = null;
      if (!partnerId) return;
      hqStatus.value = 'loading';
      try {
        const list = await valetAPI.headquartersOfPartner(partnerId);
        if (partnerId !== props.partnerId) return;
        headquarters.value = list;
        headquarterId.value = list[0]?.id || '';
        hqStatus.value = 'ok';
      } catch (e) {
        console.error('Sedi del partner non lette:', e);
        hqStatus.value = 'error';
      }
    }, { immediate: true });

    const origin = computed(() => ({
      address: clean(props.address.address),
      street_number: clean(props.address.street_number),
      cap: clean(props.address.cap),
      city: clean(props.address.city),
      province: clean(props.address.province).toUpperCase(),
    }));
    const addressReady = computed(() => origin.value.address && origin.value.city);

    // La richiesta di ADESSO: una risposta arrivata tardi non sovrascrive quella giusta.
    let request = 0;
    const compute = async () => {
      if (!props.partnerId || !addressReady.value || !headquarterId.value) {
        result.value = null;
        status.value = 'idle';
        return;
      }
      const mine = ++request;
      status.value = 'loading';
      try {
        const r = await valetAPI.quote({
          partnerId: props.partnerId,
          origin: origin.value,
          headquarterId: headquarterId.value,
        });
        if (mine !== request) return;
        result.value = r;
        status.value = 'ok';
      } catch (e) {
        if (mine !== request) return;
        result.value = null;
        serverError.value = e?.response?.data?.message || '';
        status.value = 'error';
      }
    };

    let timer = null;
    watch([origin, headquarterId], () => {
      clearTimeout(timer);
      timer = setTimeout(compute, 400);
    }, { deep: true, immediate: true });
    onBeforeUnmount(() => clearTimeout(timer));

    // La riga di listino da usare: uno, due, o «tre o più».
    const priceTier = computed(() => Math.min(3, Math.max(1, Number(pickup.value) || 1)));
    const option = computed(
      () => result.value?.options?.find((o) => o.pickups_per_year === priceTier.value) || null
    );
    const numbers = computed(
      () => (option.value ? valetAPI.packageNumbers(option.value, months.value, pickup.value) : null)
    );

    const problem = computed(() => {
      if (!props.partnerId) return 'Scegli il partner del pacchetto';
      if (!addressReady.value) return "Completa l'indirizzo del cliente: il prezzo dipende dalla distanza dalla sede.";
      if (hqStatus.value === 'loading') return 'Carico le sedi del partner…';
      if (hqStatus.value === 'error') return 'Non riesco a leggere le sedi del partner.';
      if (!headquarterId.value) return 'Il partner non ha sedi: il prezzo non si può calcolare.';
      if (status.value === 'loading' || status.value === 'idle') return 'Calcolo del prezzo in corso…';
      if (status.value === 'error') {
        return `Non riesco a calcolare la distanza${serverError.value ? ` (${serverError.value})` : ''}: controlla l'indirizzo.`;
      }
      if (!result.value?.band) return 'Per questa distanza il listino valet non ha una fascia: il pacchetto non si può proporre.';
      if (!numbers.value || numbers.value.simbaTotal === null) {
        return `Il listino non ha il prezzo per ${priceTier.value === 3 ? '3 o più' : priceTier.value} pickup in questa fascia: compilalo nel dettaglio del partner.`;
      }
      if (!numbers.value.valetTotal) return 'Con questa durata il pacchetto non comprende nessun valet: aumenta i mesi.';
      return '';
    });

    watch([problem, numbers], () => {
      emit('change', {
        problem: problem.value,
        data: problem.value ? null : {
          pickup: pickup.value,
          months: numbers.value.months,
          valetTotal: numbers.value.valetTotal,
          simbaTotal: numbers.value.simbaTotal,
          sellerTotal: numbers.value.sellerTotal,
          bandId: result.value.band.id,
          includedKm: result.value.included_km,
        },
      });
    }, { immediate: true });

    const hqLabel = (h) => [h.name, [h.address, h.street_number].filter(Boolean).join(' '), h.city]
      .filter(Boolean)
      .join(' · ');
    const bandLabel = (b) => (b.to_km === null ? `oltre ${b.from_km} km` : `${b.from_km}–${b.to_km} km`);
    const euro = (v) => euroFmt.format(v);

    return {
      uid, headquarters, headquarterId, pickup, months, DURATE, status, result, numbers, problem,
      compute, hqLabel, bandLabel, euro,
    };
  },
};
</script>

<style scoped>
.vp { display: flex; flex-direction: column; gap: 10px; }
.vp__months { width: 200px; }
.vp__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 16px; }
.vp__f { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.vp__f--wide { grid-column: 1 / -1; }
.vp__f label, .vp__lbl { font-size: 12px; font-weight: 600; color: var(--text-secondary); }
.vp__f :deep(.el-input-number) { width: 100%; }
.vp__f :deep(.el-radio-group) { align-self: flex-start; }
.vp__where { margin: 0; min-height: 16px; font-size: 12px; color: var(--text-tertiary); }
.vp__stats {
  display: flex; flex-wrap: wrap; gap: 6px 18px;
  font-size: 12.5px; color: var(--text-secondary); font-variant-numeric: tabular-nums;
}
.vp__stats b { color: var(--text-primary); }
.vp__problem { margin: 0; font-size: 12.5px; font-weight: 600; color: var(--state-attn); }
@media (max-width: 560px) {
  .vp__grid { grid-template-columns: 1fr; }
}
</style>
